import React, { useCallback, useEffect, useState } from 'react';
import Login from './Login';
import ProjectsHome from './ProjectsHome';
import ProjectWorkspace from './ProjectWorkspace';
import { checkSession, fetchProjects, logout } from './api';
import { getTheme } from './theme';

function Admin() {
  const [status, setStatus] = useState('loading');
  const [session, setSession] = useState(null);
  const [isDark, setIsDark] = useState(() => {
    const saved = localStorage.getItem('adminTheme');
    if (saved) return saved === 'dark';
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  const [projects, setProjects] = useState([]);
  const [activeProject, setActiveProject] = useState(null);
  const [loadError, setLoadError] = useState('');

  const theme = getTheme(isDark);

  const toggleTheme = () => {
    setIsDark((prev) => {
      const next = !prev;
      localStorage.setItem('adminTheme', next ? 'dark' : 'light');
      return next;
    });
  };

  const refreshProjects = useCallback(async () => {
    setLoadError('');
    try {
      const list = await fetchProjects();
      setProjects(list);
    } catch (err) {
      setLoadError(err.message || 'Failed to load projects');
    }
  }, []);

  useEffect(() => {
    let active = true;
    checkSession().then(async (data) => {
      if (!active) return;
      if (data && data.authenticated) {
        setSession(data);
        setStatus('in');
        try {
          const list = await fetchProjects();
          if (active) setProjects(list);
        } catch (err) {
          if (active) setLoadError(err.message || 'Failed to load projects');
        }
      } else {
        setSession(data || null);
        setStatus('out');
      }
    });
    return () => {
      active = false;
    };
  }, []);

  if (status === 'loading') {
    return (
      <div
        style={{
          minHeight: '100dvh',
          backgroundColor: theme.bg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: theme.textMuted,
          fontFamily: theme.font,
          fontSize: 14,
        }}
      >
        Loading...
      </div>
    );
  }

  if (status === 'out') {
    return (
      <Login theme={theme} onToggleTheme={toggleTheme} />
    );
  }

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: theme.bg,
        fontFamily: theme.font,
        color: theme.text,
      }}
    >
      <header
        style={{
          borderBottom: `1px solid ${theme.border}`,
          backgroundColor: theme.surface,
          padding: '14px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="/60-DB.png" alt="60 Decibels" style={{ height: 28, objectFit: 'contain' }} />
          <span style={{ fontSize: 14, fontWeight: 600, letterSpacing: '-0.01em' }}>
            Survey Links
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button
            type="button"
            onClick={toggleTheme}
            style={{
              border: `1px solid ${theme.border}`,
              background: theme.surfaceMuted,
              color: theme.textMuted,
              borderRadius: 999,
              padding: '7px 12px',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            {isDark ? 'Light' : 'Dark'}
          </button>
          <span style={{ fontSize: 13, color: theme.textMuted }}>
            {session.user && session.user.email}
          </span>
          <button
            type="button"
            onClick={async () => {
              await logout();
              setSession(null);
              setStatus('out');
              setActiveProject(null);
            }}
            style={{
              border: `1px solid ${theme.border}`,
              background: 'transparent',
              color: theme.textMuted,
              borderRadius: 8,
              padding: '7px 12px',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            Sign out
          </button>
        </div>
      </header>

      <main style={{ maxWidth: 920, margin: '0 auto', padding: '32px 20px 64px' }}>
        {!session.dbEnabled && (
          <div
            style={{
              backgroundColor: 'rgba(234,179,8,0.10)',
              border: '1px solid rgba(234,179,8,0.35)',
              color: '#A16207',
              borderRadius: theme.radius,
              padding: 16,
              marginBottom: 20,
              fontSize: 14,
            }}
          >
            Database is not configured. Set <code>DATABASE_URL</code> (Heroku Postgres) so projects
            and links can be saved.
          </div>
        )}

        {loadError && (
          <div
            style={{
              backgroundColor: 'rgba(220,38,38,0.08)',
              border: '1px solid rgba(220,38,38,0.35)',
              color: theme.danger,
              borderRadius: theme.radius,
              padding: 16,
              marginBottom: 20,
              fontSize: 14,
            }}
          >
            {loadError}
          </div>
        )}

        {activeProject ? (
          <ProjectWorkspace
            theme={theme}
            project={activeProject}
            onBack={() => {
              setActiveProject(null);
              refreshProjects();
            }}
            onProjectUpdated={(updated) => {
              setActiveProject(updated);
              refreshProjects();
            }}
            onProjectDeleted={() => {
              setActiveProject(null);
              refreshProjects();
            }}
          />
        ) : (
          <ProjectsHome
            theme={theme}
            projects={projects}
            onRefresh={refreshProjects}
            onOpen={setActiveProject}
          />
        )}
      </main>
    </div>
  );
}

export default Admin;
